import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bell, CheckCircle, XCircle } from '@phosphor-icons/react';

import { getPublicNotice, publicDeregister } from '@/api/notices_public';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Public notice page. Routed at /n/:token, OUTSIDE RequireAuth — the token in
// the URL is the credential (same posture as /pay/:token). A member sees what
// the notice is for and, when the club allows it, can say "I'm not continuing"
// (self-deregister). Paying is not wired here yet; the amount is shown so the
// member knows what's owed. Design-system rules apply — this is a
// supporter-facing surface.
export function PublicNotice() {
  const { token = '' } = useParams();
  const { t, i18n } = useTranslation('common');
  const [confirming, setConfirming] = useState(false);

  const q = useQuery({
    queryKey: ['public-notice', token],
    queryFn: () => getPublicNotice(token),
    enabled: !!token,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });

  const deregM = useMutation({
    mutationFn: () => publicDeregister(token, {}),
    onSuccess: () => void q.refetch(),
  });

  function money(minor: number, currency?: string): string {
    const cur = currency || 'SEK';
    try {
      return new Intl.NumberFormat(i18n.language || 'sv-SE', { style: 'currency', currency: cur }).format(
        minor / 100,
      );
    } catch {
      return `${(minor / 100).toFixed(2)} ${cur}`;
    }
  }

  if (q.isPending) {
    return <Shell>{<p className="text-sm text-foreground/60">{t('common.loading')}</p>}</Shell>;
  }
  if (q.isError || !q.data) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-2 text-center">
          <XCircle weight="bold" className="h-8 w-8 text-destructive" />
          <p className="text-sm text-foreground/70">{t('publicNotice.notFound')}</p>
        </div>
      </Shell>
    );
  }

  const n = q.data;
  const period = n.period ? t(`notices.period.${n.period}`) : '';
  const closed = n.state === 'deregistered' || n.state === 'declined' || n.state === 'cancelled';
  const paid = n.state === 'paid';
  const canDereg = n.self_dereg_enabled && (n.state === 'sent' || n.state === 'overdue');

  return (
    <Shell>
      <div className="flex flex-col gap-4">
        <header className="flex flex-col gap-1">
          <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/50">
            <Bell weight="fill" className="h-4 w-4" />
            {t('publicNotice.eyebrow')}
          </span>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{n.team_name}</h1>
          <p className="text-sm text-foreground/70">
            {t('publicNotice.forMember', { name: n.member_name })}
            {period ? ` · ${period}` : ''}
            {n.year ? ` ${n.year}` : ''}
          </p>
        </header>

        {paid && (
          <div className="flex items-center gap-2 rounded-xl border border-border bg-background p-3 text-sm">
            <CheckCircle weight="fill" className="h-5 w-5 text-primary" />
            {t('publicNotice.paidNote')}
          </div>
        )}
        {closed && (
          <div className="rounded-xl border border-border bg-background p-3 text-sm text-foreground/70">
            {t(`publicNotice.closed.${n.state}`, { defaultValue: t('publicNotice.closed.deregistered') })}
          </div>
        )}

        {!paid && !closed && (
          <>
            {n.is_tryout && (
              <Badge variant="secondary" className="w-fit">
                {t('publicNotice.tryout')}
              </Badge>
            )}
            <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-foreground/70">{t('publicNotice.total')}</span>
                <span className="font-heading text-2xl font-bold tabular-nums">
                  {money(n.total_minor, n.currency)}
                </span>
              </div>
              {n.membership_minor > 0 && (
                <Row label={t('publicNotice.membership')} value={money(n.membership_minor, n.currency)} />
              )}
              {n.training_minor > 0 && (
                <Row label={t('publicNotice.training')} value={money(n.training_minor, n.currency)} />
              )}
              {n.addon_minor > 0 && (
                <Row label={t('publicNotice.addon')} value={money(n.addon_minor, n.currency)} />
              )}
              {n.due_date && (
                <Row
                  label={t('publicNotice.dueDate')}
                  value={new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                    new Date(n.due_date),
                  )}
                />
              )}
            </div>

            <p className="text-sm text-foreground/60">{t('publicNotice.payHint')}</p>

            {canDereg && (
              <div className="flex flex-col gap-2 border-t border-border pt-4">
                {!confirming ? (
                  <Button variant="outline" onClick={() => setConfirming(true)}>
                    {n.self_dereg_label || t('publicNotice.deregCta')}
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-foreground/70">{t('publicNotice.deregConfirm')}</p>
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        disabled={deregM.isPending}
                        onClick={() => deregM.mutate()}
                      >
                        {deregM.isPending ? t('publicNotice.deregging') : t('publicNotice.deregConfirmCta')}
                      </Button>
                      <Button variant="outline" onClick={() => setConfirming(false)}>
                        {t('publicNotice.cancel')}
                      </Button>
                    </div>
                  </div>
                )}
                {deregM.isError && <p className="text-sm text-destructive">{t('publicNotice.deregFailed')}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-foreground/60">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-background p-6 shadow-lg">
        {children}
      </div>
    </div>
  );
}
